import { PlusCircleIcon as AddIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import a from 'indefinite'
import { useRecoilState } from 'recoil'
import { isUserAdminState } from '../store/DomainStore'
import DropdownLinks from './DropdownLinks'
import IconWithTitle from './icons/IconWithTitle'

function getNewAccountLinks() {
    return [
        // DS, 2024-10-22: X Changed their mind again so I've undone the change outlined below.
        // DC, 2024-10-17: X have changed our Pro Plan to limit the number of authorised X profiles.
        // See https://arwen-ai.atlassian.net/browse/AA-1014
        // We've decided to disable adding new X profiles.
        // You can navigate to /account/new/twitter to create a new profile if you have to.
        {
            title: 'Twitter/X profile',
            to: '/account/new/twitter',
            icon: <IconWithTitle icon="fab fa-x-twitter text-4xl mb-2" />,
            isAdminLink: false
        },
        {
            title: 'Facebook page',
            to: '/account/new/facebook_page',
            icon: <IconWithTitle icon="fab fa-facebook text-4xl mb-2" />,
            isAdminLink: false
        },
        {
            title: 'YouTube channel',
            to: '/account/new/youtube',
            icon: <IconWithTitle icon="fab fa-youtube text-4xl mb-2" />,
            isAdminLink: false
        },
        {
            title: 'Instagram profile (Individuals)',
            to: '/account/new/instagram_direct',
            icon: <IconWithTitle icon="fab fa-instagram text-4xl mb-2" />,
            isAdminLink: false
        },
        {
            title: 'Instagram profile (Organisations)',
            to: '/account/new/instagram',
            icon: <IconWithTitle icon="fab fa-instagram text-4xl mb-2" />,
            isAdminLink: false
        },
        {
            title: 'TikTok Ad Account',
            to: '/account/new/tiktok_ad',
            icon: <IconWithTitle icon="fab fa-tiktok text-4xl" title="Ad" />,
            isAdminLink: false
        },
        {
            title: 'TikTok User Account',
            to: '/account/new/tiktok_user',
            icon: <IconWithTitle icon="fab fa-tiktok text-4xl" title="User" />,
            isAdminLink: false
        }
    ]
}

/**
 *This function is not used anywhere in the project. Is it necessary or can we delete it?
 */
function NewAccountDropDown() {
    const newAccountLinks = getNewAccountLinks()

    return (
        <DropdownLinks
            title="Add social profile"
            links={newAccountLinks}
            icon={<AddIcon className="w-5 h-5 text-gray-950" />}
        />
    )
}

/**
 * Monitored accounts have no authorisation — content arrives via scraping
 * only. Creating one is admin-only; first line then sets up keywords and
 * moderation boundaries before scraping is configured.
 */
function getMonitoredAccountLinks() {
    return [
        {
            title: 'Instagram profile',
            to: '/account/new/monitored/instagram',
            icon: <IconWithTitle icon="fab fa-instagram text-4xl mb-2" />
        },
        {
            title: 'Facebook page',
            to: '/account/new/monitored/facebook',
            icon: <IconWithTitle icon="fab fa-facebook text-4xl mb-2" />
        },
        {
            title: 'TikTok user account',
            to: '/account/new/monitored/tiktok_user',
            icon: <IconWithTitle icon="fab fa-tiktok text-4xl mb-2" />
        },
        {
            title: 'YouTube channel',
            to: '/account/new/monitored/youtube',
            icon: <IconWithTitle icon="fab fa-youtube text-4xl mb-2" />
        }
    ]
}

type AccountLinkCardProps = {
    title: string
    to: string
    icon: JSX.Element
    /** Prefix for the card label, e.g. "Add" or "Monitor". */
    verb: string
    /** Small label along the bottom of the card, e.g. "Monitoring". */
    badge?: string
}

function AccountLinkCard({ title, to, icon, verb, badge }: AccountLinkCardProps) {
    return (
        <div className="flex flex-col items-center m-4">
            <Link
                to={to}
                type="button"
                className={`
                  flex
                  flex-col
                  items-center
                  justify-center
                  relative
                  w-36
                  h-36
                  border
                  border-ink-200
                  border-solid
                  rounded-md
                  p-4
                  text-center
                  shadow-sm
                  hover:border-ink-300
                  hover:bg-ink-50
                  focus:outline-none
                  focus:ring-2
                  focus:ring-offset-2
                  focus:ring-arwen-500
                  no-underline
                `}
                data-cy={`goto_${verb.toLowerCase()}_${title.toLowerCase()}_button`}
            >
                {icon}
                <span className="text-sm font-medium text-gray-900">
                    {verb} {a(title)}
                </span>
                {badge ? (
                    <span className="absolute bottom-1.5 text-[10px] uppercase tracking-wider text-gray-500">
                        {badge}
                    </span>
                ) : null}
            </Link>
        </div>
    )
}

export function NewAccountButtons() {
  const [admin] = useRecoilState(isUserAdminState)
  const newAccountLinks = getNewAccountLinks().filter((link) => {
    if (!admin && link.isAdminLink) {
      return false
    }
    return true
  })

  return (
    <div className="flex flex-row justify-center flex-wrap">
            <div className="flex flex-row justify-center flex-wrap">
                {newAccountLinks.map((link) => (
                    <AccountLinkCard
                        key={link.title}
                        title={link.title}
                        to={link.to}
                        icon={link.icon}
                        verb="Add"
                    />
                ))}
            </div>
            {admin ? (
                <div className="mt-8">
                    <h3 className="text-center font-sans text-lg font-medium text-ink-900">
                        Monitored profiles
                    </h3>
                    <p className="text-center text-sm text-gray-500">
                        No authorisation — comments are collected by scraping, so Arwen can
                        analyse but not act on them. Admins only.
                    </p>
                    <div className="flex flex-row justify-center flex-wrap">
                        {getMonitoredAccountLinks().map((link) => (
                            <AccountLinkCard
                                key={link.title}
                                title={link.title}
                                to={link.to}
                                icon={link.icon}
                                verb="Add"
                                badge="Monitoring"
                            />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default NewAccountDropDown
